import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartErrorEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';



/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  // Serialises the Spicers quantity write only (#updateSpicersQuantity). The
  // index-addressed updateQuantity() path does NOT go through this chain.
  #cartWriteChain = Promise.resolve();
  // Click order. The stock lookup ahead of the write is not serialised, so jobs
  // can reach the chain out of order; a superseded job is dropped at the head.
  #clickSeq = 0;
  #latestSeqByLine = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Initial cart verification on page load
    this.#verifyCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Verify Cart items against Spicers API to ensure prices and HMACs match the current logged in user.
   */
  #verifyCart() {
    window.fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        console.log('[Cart] Shopify /cart.js Response:', cart);
        if (!cart || !cart.items || cart.items.length === 0) return;
        let line = 0;
        const data = cart.items.map(i => {
          return {
            line: ++line,
            cust_price: i.properties?._cust_price,
            price: i.properties?._price,
            hash: i.properties?._hmac
          };
        });
        const theme = window.theme;
        if (!theme || !theme.ENV || !theme.ENV.apiUrl) return;

        window.fetch(`${theme.ENV.apiUrl}/cart/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': '1'
          },
          body: JSON.stringify(data)
        })
        .then(res => res.json())
        .then(result => {
           console.log('[Cart] Spicers /cart/verify Response:', result);
           if (result && !result.valid) {
             const checkoutBtns = document.querySelectorAll('[name="checkout"], .cart__checkout-button');
             checkoutBtns.forEach(btn => btn.setAttribute('disabled', 'disabled'));

             result.invalidLine.forEach(l => {
                this.#handleCartError(l, { errors: 'Account changed. Please update quantity to re-price.' });
             });
           }
        })
        .catch(console.error);
      });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * Validates MOQ (using variant.metafields.custom.order_multiple, same source as Cart Page)
   * and LM (lineal metre) rules before committing any quantity change. Reuses the
   * shared theme.quantityRules helpers (assets/application.js) so the minicart
   * follows the exact same whole-number/roll-multiple rules as every other
   * add-to-cart / cart-update surface (PDP, bulk-order, Quick Order, cart page).
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  async #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Find the quantity input for this line to read Cart-Page-compatible data attributes
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const input = quantitySelector?.querySelector('input[ref="quantityInput"], input[data-cart-line]');
    const material = input?.dataset?.material;
    const prm = input?.dataset?.prm;
    const itemKey = input?.dataset?.itemKey;

    const seq = ++this.#clickSeq;
    this.#latestSeqByLine.set(line, seq);

    // --- MOQ / LM validation (mirrors Cart Page blur handler exactly) ---
    const hasMultiplierRestriction = input?.dataset?.restrictQty !== undefined;
    const multiplier = hasMultiplierRestriction ? (parseFloat(input.dataset.multiplier) || 1) : 1;
    const lmRaw = input?.dataset?.lm ? parseFloat(input.dataset.lm) : 0;
    const isLmProduct = lmRaw > 0;
    // lastInput: last server-confirmed qty — used to revert input when user cancels popup
    const lastInput = parseFloat(input?.dataset?.lastInput) || parseFloat(input?.defaultValue) || quantity;

    const applyQty = (qty) => {
      const revertTo = input ? (input.dataset.lastInput ?? input.value) : undefined;
      if (input) { input.value = qty; input.dataset.lastInput = qty; }
      if (material && prm) {
        this.#updateSpicersQuantity({ line, quantity: qty, material, prm, itemKey, seq, revertTo });
      } else {
        this.updateQuantity({ line, quantity: qty, action: 'change' });
      }
      const lineItemRow = this.refs.cartItemRows?.[line - 1];
      if (lineItemRow) {
        const textComponent = lineItemRow.querySelector('text-component');
        textComponent?.shimmer();
      }
    };

    if (isLmProduct) {
      // Whole numbers always accepted; a decimal must be an exact roll
      // multiple, otherwise block and offer the round-down/round-up choice
      // (same UX as PDP/bulk-order/Quick Order/cart page).
      const lmEval = window.theme?.quantityRules?.evaluateLmQuantity(quantity, lmRaw);
      if (lmEval && !lmEval.accepted) {
        if (input) input.value = lastInput;
        window.theme.quantityRules.promptLmRollChoice(lmRaw, lmEval, (chosenValue) => {
          this.#checkStockStatusAndApply(chosenValue, material, prm, itemKey, line, lastInput, applyQty);
        });
        return;
      }
    } else if (multiplier > 1) {
      const safeQty = Math.round(quantity * 1000);
      const safeMult = Math.round(multiplier * 1000);
      const isViolation = safeQty % safeMult !== 0;

      if (isViolation) {
        const nextQty = Math.ceil(safeQty / safeMult) * multiplier;
        const msg = `Order quantity must be in multiples of ${multiplier} for this product - would you like to update order quantity to ${nextQty}?`;

        this.#showMoqConfirm(msg, (accepted) => {
          if (accepted) {
            this.#checkStockStatusAndApply(nextQty, material, prm, itemKey, line, lastInput, applyQty);
          } else {
            if (input) input.value = lastInput;
          }
        });
        return;
      }
    }
    // --- End MOQ / LM validation ---

    // --- Stock Validation ---
    this.#checkStockStatusAndApply(quantity, material, prm, itemKey, line, lastInput, applyQty);
  }

  async #checkStockStatusAndApply(quantity, material, prm, itemKey, line, lastInput, applyQty) {
    if (!material) {
       applyQty(quantity);
       return;
    }

    const input = this.refs.quantitySelectors?.[line - 1]?.querySelector('input');
    const theme = window.theme;
    if (!theme || !theme.ENV || !theme.ENV.apiUrl) {
       applyQty(quantity);
       return;
    }

    const state = theme.utils?.getCookie('user-state') || 'NSW';
    const customer = window.customer?.sapId || '';
    const url = `${theme.ENV.apiUrl}/stock-template-by-material?materialNo=${material}&qty=${quantity}&postcode=${state}${customer ? `&customer=${customer}` : ''}`;

    const wrapperSelector = 'td, .cart-item__quantity-wrapper, .cart-items__quantity, .quantity-selector';
    if (input) input.closest(wrapperSelector)?.classList.add('loading');

    try {
      const response = await window.fetch(url, { headers: { 'ngrok-skip-browser-warning': '1' } });
      const result = await response.json();
      if (input) input.closest(wrapperSelector)?.classList.remove('loading');

      const soh = result?.data?.soh ?? result?.soh ?? 0;
      if (soh < quantity) {
         // BACKORDER POPUP DISABLED:
         // The customer service team picks it up, contacts the customer, and flags that the item needs to be backordered.
         applyQty(quantity);
         return;
      }
      applyQty(quantity);
    } catch (err) {
      console.error(err);
      if (input) input.closest(wrapperSelector)?.classList.remove('loading');
      applyQty(quantity); // proceed anyway on network error
    }
  }

  /**
   * Calls the Spicers pricing API to get a fresh HMAC for the updated quantity,
   * then updates the cart line properties (qty + _hmac) via /cart/change.js.
   * Shopify's line quantity stays at 1 — the real qty lives in properties.qty.
   *
   * @param {Object} config
   * @param {number} config.line - 1-indexed cart line
   * @param {number} config.quantity - New qty entered by the user
   * @param {string} config.material - SKU / material number
   * @param {string} config.prm - PRM value
   * @param {string} config.itemKey - Shopify cart item key
   * @param {number} [config.seq] - Click order, used to drop superseded writes
   * @param {string|number} [config.revertTo] - Input value to restore on failure
   */
  #updateSpicersQuantity(config) {
    const run = () => {
      // A newer click for this line landed while this job was queued. Quantities
      // are absolute, so the stale one must not be written.
      if (config.seq !== undefined && this.#latestSeqByLine.get(config.line) !== config.seq) return;
      return this.#runSpicersQuantityUpdate(config);
    };
    const next = this.#cartWriteChain.then(run, run);
    this.#cartWriteChain = next.catch(() => {});
    return next;
  }

  async #runSpicersQuantityUpdate({ line, quantity, material, prm, itemKey, revertTo }) {
    const theme = window.theme;
    if (!theme) return;

    this.#disableCartItems();

    try {
      const customer = (window.customer?.sapId) || '143282';
      const captchaKey = theme.ENV?.captcha_site_key || '6LextFosAAAAAH3nCeeqSd1g50XqeAfURZOt8Z6a';

      // Get a fresh reCAPTCHA token only if we are not removing the item (quantity > 0)
      let token = '';
      if (quantity > 0) {
        try {
          // grecaptcha.ready has no reject path, so a blocked script would hang
          // here forever and, now that writes are serialised, freeze the cart.
          await Promise.race([
            new Promise((resolve) => {
              grecaptcha.ready(() => {
                grecaptcha.execute(captchaKey, { action: 'submit' }).then((t) => {
                  token = t;
                  resolve();
                });
              });
            }),
            new Promise((resolve) => setTimeout(resolve, 8000)),
          ]);
        } catch (e) {
          console.warn('[Cart Drawer] reCAPTCHA failed, skipping pricing API:', e);
        }
      }

      let newProperties = null;
      let newHmac = null;

      // Shopify derives a line's key from its properties, so every write gives
      // the line a new key. Resolve the key from the current cart instead of the
      // one rendered into the DOM, which goes stale the moment a write lands and
      // then fails with "no valid id or line parameter".
      let resolvedKey = itemKey;
      let currentItem = null;
      try {
        const freshCartRes = await window.fetch('/cart.js', { cache: 'no-store' });
        const freshCart = await freshCartRes.json();
        currentItem = freshCart.items?.[line - 1] || null;
        // items[line - 1] is a render-time index read against a write-time cart.
        // If it no longer points at the same variant the cart moved underneath us,
        // and writing would target another line. Drop it and fail below instead.
        const domVariantId = itemKey ? String(itemKey).split(':')[0] : null;
        if (currentItem && domVariantId && String(currentItem.variant_id) !== domVariantId) {
          currentItem = null;
        }
        if (currentItem?.key) resolvedKey = currentItem.key;
        // Seed from the line's current properties. /cart/change.js REPLACES the
        // properties object rather than merging, so anything the pricing call
        // does not return (_material, _prm, _uomName, _sheet, ...) would be lost.
        // The price triple it carries is only ever written alongside a fresh
        // quote — see the guard before finalProperties.
        if (currentItem?.properties) newProperties = { ...currentItem.properties };
      } catch (keyErr) {
        console.warn('[Cart Drawer] could not refresh cart line key:', keyErr);
      }

      if (token && quantity > 0) {
        const apiUrl = theme.ENV?.apiUrl;
        const url = `${apiUrl}/cart?token=${token}`;
        const session = window.ShopifyAnalytics?.lib?.user?.()?.traits?.()?.uniqToken || '';

        const cartData = {
          variants: [{ variantId: null, qty: quantity, material: material, prm: prm }],
          session: session,
          token: 'KIi16KkJ3HEjwydO7S582tKkESRZJsaJDlH2ckDXZ1OJjmHp6W',
          customer: customer
        };

        const cartItem = currentItem;
        if (cartItem) {
          cartData.variants[0].variantId = cartItem.variant_id;
          // Preserve the existing cart item properties as the base
          newProperties = { ...(cartItem.properties || {}) };

          const result = await window.fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cartData)
          }).then(r => r.json());

          const apiItem = Array.isArray(result) ? result[0] : result;
          
          console.log('[Cart] Spicers API Response for Quantity Change:', result);
          console.log('[Cart] Item Data being set:', apiItem);
          
          // Catch Spicers API errors directly so they don't get bypassed silently
          if (apiItem && apiItem.error) {
             throw new Error(apiItem.error);
          }

          if (apiItem && apiItem.properties) {
            Object.entries(apiItem.properties).forEach(([k, v]) => {
              newProperties = newProperties || {};
              newProperties[k] = v;
            });
            newHmac = apiItem.hmac;
          }
        }
      }

      // _cust_price/_price are the quoted total for a specific qty, and
      // /cart/verify only re-checks that triple against its hmac — it never sees
      // qty. Writing a new qty without a fresh quote would leave the line priced
      // for the old quantity AND still pass verification, so the customer would
      // check out silently underpriced. Refuse the write instead.
      if (quantity > 0 && !newHmac) {
        throw new Error('Could not confirm pricing for the new quantity. Please try again.');
      }

      const finalProperties = {
        ...(newProperties || {}),
        qty: quantity,
      };
      // To prevent showing the old qty alongside _qty
      Object.assign(finalProperties, (newHmac ? { _hmac: newHmac } : {}));

      // Use the full cart item key (variant_id:hash) if available; Shopify requires the complete key string
      let changePayload;
      if (quantity === 0) {
        // If removing the item, we must pass quantity: 0 at the root level to Shopify
        changePayload = resolvedKey ? { id: resolvedKey, quantity: 0 } : { line: line, quantity: 0 };
      } else {
        changePayload = resolvedKey
          ? { id: resolvedKey, properties: finalProperties }
          : { line: line, properties: finalProperties };
      }

      const changeResponse = await window.fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changePayload)
      });
      const changeResult = await changeResponse.json();

      if (changeResponse.status === 422 || (changeResult && changeResult.status && changeResult.message)) {
         const errorMsg = changeResult.description || changeResult.message || '';
         if (errorMsg.toLowerCase().includes('different account')) {
            this.#verifyCart();
            return;
         }
         throw new Error(errorMsg);
      }

      // Dispatch cart update event to update header cart count & price dynamically
      const newCartItemCount = changeResult?.items?.length || 0;
      const newCartTotal = changeResult?.total_price || 0;
      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          carttotal: newCartTotal,
        })
      );

      // Re-render the cart section so displayed qty and price reflect the change
      sectionRenderer.renderSection(this.sectionId, { cache: false });

    } catch (err) {
      console.error('[Cart Drawer] Spicers qty update failed:', err);
      // The cart was not changed, so put the displayed quantity back.
      if (revertTo !== undefined) {
        const revertInput = this.refs.quantitySelectors?.[line - 1]?.querySelector('input');
        if (revertInput) {
          revertInput.value = revertTo;
          revertInput.dataset.lastInput = revertTo;
        }
      }
      const friendlyMsg =
        err.message === 'Bad Request'
          ? "We couldn't update the quantity. Please try again in a moment."
          : err.message || 'Error updating cart item';
      this.#handleCartError(line, { errors: friendlyMsg });
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors || parsedResponseText.status >= 400 || parsedResponseText.message) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
            carttotal: parsedResponseText.total_price,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // If it's a global 422 Unprocessable Content error, dispatch globally and abort
    if (parsedResponseText.status === 422 || (!parsedResponseText.errors && parsedResponseText.description)) {
       const msg = parsedResponseText.description || parsedResponseText.message;
       
       if (msg && msg.toLowerCase().includes('different account')) {
          this.#verifyCart();
          return;
       }

       if (msg) window.dispatchEvent(new CartErrorEvent(this.id, msg));
       return;
    }

    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    // If line item error containers don't exist (e.g. Cart Drawer), fallback to global error
    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
       window.dispatchEvent(new CartErrorEvent(this.id, parsedResponseText.errors));
       return;
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;
    
    // If there was an error adding to the cart, the cart hasn't changed,
    // so we skip re-rendering to prevent overwriting the error UI.
    if (event.detail?.data?.didError) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  /**
   * Show an MOQ confirmation modal as a native <dialog> appended to <body>.
   * This puts it in the browser's top layer, guaranteed above the Cart Drawer.
   * @param {string} message
   * @param {function(boolean): void} callback
   */
  #showMoqConfirm(message, callback) {
    if (window.vex && window.vex.dialog) {
      let appendLoc = 'body';
      let wrapperDialog = null;
      if (document.querySelector('.cart-drawer__dialog[open]')) {
        wrapperDialog = document.createElement('dialog');
        const uniqueId = 'moq-vex-wrapper-cart-' + Date.now();
        wrapperDialog.id = uniqueId;
        wrapperDialog.style.cssText = 'width: 100vw; height: 100vh; background: transparent; border: none; padding: 0; margin: 0; max-width: none; max-height: none; overflow: hidden;';
        document.body.appendChild(wrapperDialog);
        wrapperDialog.showModal();
        appendLoc = '#' + uniqueId;
      }
      window.vex.dialog.confirm({
        message: message,
        appendLocation: appendLoc,
        callback: function (value) {
          callback(value);
          if (wrapperDialog) {
            setTimeout(() => {
              if (wrapperDialog.open) wrapperDialog.close();
              wrapperDialog.remove();
            }, 500);
          }
        }
      });
    } else {
      const accepted = window.confirm(message);
      callback(accepted);
    }
  }

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}